import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  LinearProgress,
  Fade,
  Zoom,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import * as XLSX from 'xlsx';
import useStore from '../store/useStore';

// onReviewChanges (optional): opens the admin review of pending AlfaCRM
// differences; the dialog closes itself before calling it.
export default function AlfaSyncDialog({
  open,
  onClose,
  onSyncSuccess,
  onReviewChanges,
}) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({
    index: 0,
    total: 0,
    currentName: '',
  });
  const [processedList, setProcessedList] = useState([]);

  // Config State
  const [alfaConfig, setAlfaConfig] = useState({
    domain: '',
    email: '',
    apiKey: '',
    branchId: '1',
  });

  const { socket, language } = useStore();
  const isEn = language === 'en';

  useEffect(() => {
    if (open) {
      loadAlfaConfig();
    }
  }, [open]);

  useEffect(() => {
    if (socket) {
      const handleProgress = (data) => {
        setProgress(data);
        if (data.currentName) {
          setProcessedList((prev) => [
            { id: Date.now() + Math.random(), name: data.currentName },
            ...prev.slice(0, 4),
          ]);
        }
      };
      socket.on('import_progress', handleProgress);
      return () => socket.off('import_progress', handleProgress);
    }
  }, [socket]);

  const loadAlfaConfig = async () => {
    try {
      const res = await fetch('/api/admin/alfacrm-config', {
        credentials: 'include',
      });
      if (res.ok) {
        const config = await res.json();
        setAlfaConfig({
          domain: config.domain || '',
          email: config.email || '',
          apiKey: config.hasKey ? '********' : '',
          branchId: config.branchId || '1',
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSync = async () => {
    setLoading(true);
    setError(null);
    setResults(null);
    setProgress({ index: 0, total: 0, currentName: '' });
    setProcessedList([]);

    try {
      const res = await fetch('/api/admin/import-alfacrm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(alfaConfig),
        credentials: 'include',
      });

      const data = await res.json();
      if (res.ok) {
        setResults(data);
        if (onSyncSuccess) onSyncSuccess();
      } else {
        setError(
          data.message || (isEn ? 'Sync failed' : 'Ошибка синхронизации')
        );
      }
    } catch (err) {
      setError(isEn ? 'Network error' : 'Ошибка сети');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    if (!results || !results.importedCredentials) return;

    const ws = XLSX.utils.json_to_sheet(
      results.importedCredentials.map((c) => ({
        'Parent Name': c.name,
        'Login (Email/Username)': c.username,
        'Generated Password': c.password,
        'Student Name': c.student,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Credentials');
    XLSX.writeFile(wb, 'AlfaCRM_Credentials_Report.xlsx');
  };

  const resetAndClose = () => {
    setResults(null);
    setError(null);
    setProcessedList([]);
    onClose();
  };

  const openReview = () => {
    resetAndClose();
    onReviewChanges();
  };

  const summary = (results && results.results) || {};
  const credentials = (results && results.importedCredentials) || [];
  const attendance = results && results.attendance;
  const pendingChanges = (results && results.pendingChanges) || 0;

  const percent =
    progress.total > 0
      ? Math.round((progress.index / progress.total) * 100)
      : 0;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : resetAndClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isEn ? 'Sync with AlfaCRM' : 'Синхронизация с AlfaCRM'}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 1 }}>
          {isEn
            ? 'AlfaCRM is only read. App data is never overwritten: new data is added, differences go to the admin for review.'
            : 'AlfaCRM только читается. Данные приложения не перезаписываются: новое добавляется, расхождения попадают на проверку администратору.'}
        </Alert>

        {/* State 1: Configuration Form */}
        {!loading && !results && (
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {isEn
                ? "Enter your AlfaCRM credentials to sync students and parents. New accounts will be created for parents if they don't exist."
                : 'Введите доступ к AlfaCRM, чтобы подтянуть учеников и родителей. Для новых родителей будут созданы аккаунты.'}
            </Typography>
            <TextField
              label={
                isEn
                  ? 'Domain (e.g. demo.s20.online)'
                  : 'Домен (например, demo.s20.online)'
              }
              fullWidth
              value={alfaConfig.domain}
              onChange={(e) =>
                setAlfaConfig({ ...alfaConfig, domain: e.target.value })
              }
            />
            <TextField
              label={isEn ? 'Email (Login)' : 'Email (логин)'}
              fullWidth
              value={alfaConfig.email}
              onChange={(e) =>
                setAlfaConfig({ ...alfaConfig, email: e.target.value })
              }
            />
            <TextField
              label={isEn ? 'API Key' : 'API-ключ'}
              fullWidth
              type="password"
              value={alfaConfig.apiKey}
              onChange={(e) =>
                setAlfaConfig({ ...alfaConfig, apiKey: e.target.value })
              }
            />
            <TextField
              label={isEn ? 'Branch ID' : 'ID филиала'}
              fullWidth
              type="number"
              value={alfaConfig.branchId}
              onChange={(e) =>
                setAlfaConfig({ ...alfaConfig, branchId: e.target.value })
              }
              helperText={isEn ? 'Usually 1' : 'Обычно 1'}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}

        {/* State 2: Progress Animation */}
        {loading && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={50} thickness={4} sx={{ mb: 3 }} />
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
              {isEn ? 'Syncing with AlfaCRM...' : 'Синхронизация с AlfaCRM…'}
            </Typography>

            <Box sx={{ width: '100%', mt: 2, px: 4 }}>
              <LinearProgress
                variant={progress.total > 0 ? 'determinate' : 'indeterminate'}
                value={percent}
                sx={{ height: 12, borderRadius: 6, mb: 1 }}
              />
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {isEn ? 'Processed' : 'Обработано'}: {progress.index} /{' '}
                  {progress.total || '?'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {progress.total > 0 ? `${percent}%` : ''}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 2,
                minHeight: 180,
                bgcolor: 'grey.50',
                p: 2,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'grey.300',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontWeight: 700,
                }}
              >
                {isEn ? 'Live Feed' : 'Ход синхронизации'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {processedList.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    sx={{ fontStyle: 'italic', mt: 4 }}
                  >
                    {isEn
                      ? 'Establishing connection...'
                      : 'Подключаемся к AlfaCRM…'}
                  </Typography>
                )}
                {processedList.map((item, idx) => (
                  <Fade in={true} key={item.id}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: idx === 0 ? 'primary.50' : 'background.paper',
                        borderColor: idx === 0 ? 'primary.light' : 'grey.200',
                        transform: `scale(${1 - idx * 0.05})`,
                        opacity: 1 - idx * 0.2,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: idx === 0 ? 700 : 400,
                          color: idx === 0 ? 'primary.main' : 'text.primary',
                        }}
                      >
                        {idx === 0 ? '✓ ' : ''}
                        {item.name}
                      </Typography>
                    </Paper>
                  </Fade>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* State 3: Results & Report */}
        {results && (
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                mb: 2,
              }}
            >
              <Alert severity="success" sx={{ flexGrow: 1 }}>
                {isEn
                  ? `Sync complete! Created: ${summary.created || 0}, merged: ${summary.updated || 0}, skipped: ${summary.skipped || 0}.`
                  : `Синхронизация завершена. Создано: ${summary.created || 0}, дополнено: ${summary.updated || 0}, пропущено: ${summary.skipped || 0}.`}
              </Alert>
              {summary.errors && summary.errors.length > 0 && (
                <Alert severity="warning" sx={{ flexGrow: 1 }}>
                  {isEn
                    ? `${summary.errors.length} errors occurred.`
                    : `Ошибок: ${summary.errors.length}.`}
                </Alert>
              )}
              {credentials.length > 0 && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<DownloadIcon />}
                  onClick={downloadReport}
                >
                  {isEn ? 'Download Creds' : 'Скачать доступы'}
                </Button>
              )}
            </Box>

            {attendance && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 3,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {isEn ? 'Attendance' : 'Посещаемость'}
                </Typography>
                <Typography variant="body2">
                  {isEn ? 'Added' : 'Добавлено'}:{' '}
                  <strong>{attendance.added || 0}</strong>
                </Typography>
                <Typography variant="body2">
                  {isEn ? 'Already present' : 'Уже были'}:{' '}
                  <strong>{attendance.unchanged || 0}</strong>
                </Typography>
                <Typography
                  variant="body2"
                  color={attendance.conflicts ? 'warning.main' : undefined}
                >
                  {isEn ? 'Differences' : 'Расхождений'}:{' '}
                  <strong>{attendance.conflicts || 0}</strong>
                </Typography>
                {results.attendanceMissed > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {isEn
                      ? 'AlfaCRM customers not matched'
                      : 'Не сопоставлено клиентов AlfaCRM'}
                    : <strong>{results.attendanceMissed}</strong>
                  </Typography>
                )}
              </Paper>
            )}

            {pendingChanges > 0 && (
              <Alert
                severity="warning"
                sx={{ mb: 2, alignItems: 'center' }}
                action={
                  onReviewChanges && (
                    <Button
                      color="warning"
                      variant="contained"
                      size="small"
                      disableElevation
                      onClick={openReview}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {isEn
                        ? `Review differences (${pendingChanges})`
                        : `Проверить расхождения (${pendingChanges})`}
                    </Button>
                  )
                }
              >
                {isEn
                  ? 'AlfaCRM disagrees with the app in some places. The app data was kept — choose what to accept.'
                  : 'AlfaCRM в некоторых местах не совпадает с приложением. Данные приложения сохранены — выберите, что принять.'}
              </Alert>
            )}

            {credentials.length > 0 ? (
              <Zoom in={true}>
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    {isEn ? 'New Parent Accounts' : 'Новые аккаунты родителей'}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 2 }}
                  >
                    {isEn
                      ? 'Credentials generated for new parents.'
                      : 'Логины и пароли, созданные для новых родителей.'}
                  </Typography>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ maxHeight: 300 }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead sx={{ bgcolor: 'action.hover' }}>
                        <TableRow>
                          <TableCell>
                            {isEn ? 'Parent Name' : 'Родитель'}
                          </TableCell>
                          <TableCell>{isEn ? 'Username' : 'Логин'}</TableCell>
                          <TableCell>{isEn ? 'Password' : 'Пароль'}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {credentials.map((cred, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{cred.name}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {cred.username}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>
                              {cred.password}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Zoom>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {isEn
                  ? 'No new accounts created. Existing parents were linked.'
                  : 'Новых аккаунтов нет. Существующие родители связаны с учениками.'}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose} color="inherit" disabled={loading}>
          {results ? (isEn ? 'Close' : 'Закрыть') : isEn ? 'Cancel' : 'Отмена'}
        </Button>
        {!loading && !results && (
          <Button
            onClick={handleSync}
            variant="contained"
            startIcon={<CloudSyncIcon />}
          >
            {isEn ? 'Start Sync' : 'Начать синхронизацию'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
